import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import CryptoJS from "crypto-js";
import isequal from "lodash.isequal";
import ReportModal from "./ReportModal";

const ResultingUI = (props) => {
    const [isStartDisabled, setIsStartDisabled] = useState(false);
    const [isStopDisabled, setIsStopDisabled] = useState(true);
    const [apInstance, setApInstance] = useState(null);
    const [proctoringOptions, setProctoringOptions] = useState(props?.proctoringOptions ?? {});
    const [proctoringStatus, setProctoringStatus] = useState("");
    const [hideProctoringCtr, setHideProctoringCtr] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showReportButton, setShowReportButton] = useState(false);

    const CLIENT_ID = props.clientId || "uma0EFgW";
    const CLIENT_SECRET = props.clientSecret || "csLmxHqqQrvynZ8";

    useEffect(() => {
        if (!isequal(props?.proctoringOptions, proctoringOptions)) {
            setProctoringOptions(props.proctoringOptions);
        }
    }, [props?.proctoringOptions]);

    useEffect(() => {
        if (apInstance !== null) {
            setHideProctoringCtr(false);
            setupProctoring();
            setProctoringStatus("");
        }
    }, [proctoringOptions]);

    const getHashTestAttemptId = (testAttemptId) => {
        if (CLIENT_SECRET === null) {
            return null;
        } else {
            const secretWordArray = CryptoJS.enc.Utf8.parse(CLIENT_SECRET);
            const messageWordArray = CryptoJS.enc.Utf8.parse(testAttemptId);

            const hash = CryptoJS.HmacSHA256(messageWordArray, secretWordArray);
            const base64HashedString = CryptoJS.enc.Base64.stringify(hash);

            return base64HashedString;
        }
    };

    useEffect(() => {
        if (typeof AutoProctor !== "undefined") {
            setupProctoring();
        } else {
            window.addEventListener("apLibReady", () => {
                setupProctoring();
            });
        }

        const style = document.createElement("style");
        style.innerHTML = styleForFrontCameraStream;
        document.head.appendChild(style);

        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                mutation.addedNodes.forEach(function (node) {
                    if (node.id === "frontCameraStreamHTMLElement") {
                        var parentElement = node.parentElement;
                        if (parentElement) {
                            parentElement.classList.add("frontCameraStreamCtr");
                            const msg = document.createElement("div");
                            msg.innerHTML =
                                "Please ensure that the video below shows you and your hands throughout the test. When you submit the test on the primary device, monitoring on this device will automatically stop.";
                            msg.classList.add(
                                "text-xl",
                                "text-center",
                                "w-full",
                                "py-5",
                                "lg:py-10",
                                "px-5",
                                "bg-white",
                                "font-semibold"
                            );
                            parentElement.appendChild(msg);
                        }
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            window.removeEventListener("apLibReady", () => {
                setupProctoring();
            });
        };
    }, []);

    const getTestAttemptId = () => Math.random().toString(36).slice(2, 7);

    const setupProctoring = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        let testAttemptId = urlParams.get("testAttemptId");
        if (!testAttemptId) testAttemptId = getTestAttemptId();

        const hashedTestAttemptId = getHashTestAttemptId(testAttemptId, CLIENT_SECRET);
        const creds = {
            clientId: CLIENT_ID,
            testAttemptId: testAttemptId,
            hashedTestAttemptId: hashedTestAttemptId,
            domain: props.domain,
        };
        creds.environment = window && window.environment ? window.environment : "development";
        const autoProctorObj = window.AutoProctor;
        const apIns = new autoProctorObj(creds);
        setApInstance(apIns);
        await apIns.setup({
            ...proctoringOptions,
            testContainerId: "ap-proctoring-container",
            auxDeviceTestContainerId: "aux-device-ctr",
        });

        if (props.isAuxDevice) {
            apIns.start();
        }
    };

    const updateProctoringStatus = (status) => {
        setProctoringStatus(status);
        if (status === "Proctoring stopped") {
            document.getElementById("proctor-feedback-ctr").style.display = "none";
            setHideProctoringCtr(true);
        } else {
            document.getElementById("proctor-feedback-ctr").style.display = "block";
        }
    };

    const apMonitoringStarted = useCallback(() => {
        setIsStopDisabled(false);

        // unmount the report modal if it is mounted so that it shows the new report
        setShowReportModal(false);

        document.getElementById("ap-proctoring-msg-container").classList.add("flex", "!items-start");
        document.getElementById("ap-proctoring-msg-container").classList.remove("hidden");
        document.getElementById("ap-proctoring-container").style.display = "block";
        document.querySelectorAll(".disable-while-proctoring").forEach((el) => {
            el.disabled = true;
            el.parentElement.style.opacity = "0.4";
            el.parentElement.setAttribute("title", "Disabled during proctoring");
        });
        updateProctoringStatus(
            `Proctoring Status <span class="text-sm text-gray-300">(should change as you commit violations listed above)</span>`
        );
    }, [apInstance?.testAttemptId]);

    const apMonitoringStopped = useCallback(() => {
        const apMonitoriungMsgCtr = document.getElementById("ap-proctoring-msg-container");
        if (apMonitoriungMsgCtr) {
            apMonitoriungMsgCtr.classList.add("hidden");
            apMonitoriungMsgCtr.classList.remove("flex", "!items-start");
        }
        if (apInstance) {
            updateProctoringStatus("Proctoring stopped");
            document.querySelectorAll(".disable-while-proctoring").forEach((el) => {
                el.disabled = false;
                el.parentElement.style.opacity = "1";
                el.parentElement.removeAttribute("title");
            });
            setShowReportModal(!apInstance?.isAuxiliaryDevice);
            setShowReportButton(true);
        }
    }, [apInstance?.testAttemptId]);

    useEffect(() => {
        if (apInstance?.testAttemptId) {
            window.addEventListener("apMonitoringStarted", apMonitoringStarted);
            window.addEventListener("apMonitoringStopped", apMonitoringStopped);
        }
        return () => {
            window.removeEventListener("apMonitoringStarted", apMonitoringStarted);
            window.removeEventListener("apMonitoringStopped", apMonitoringStopped);
        };
    }, [apInstance?.testAttemptId]);

    const startTest = async () => {
        setIsStartDisabled(true);
        let startTestInterval = setInterval(() => {
            if (apInstance.setupState === "ready") {
                clearInterval(startTestInterval);
                apInstance.start();
            }
        }, 100);
    };

    const stopTest = async () => {
        setIsStartDisabled(false);
        setIsStopDisabled(true);
        await apInstance.stop();
    };

    const getReportOptions = () => {
        return {
            groupReportsIntoTabs: true,
            showSessionRecording: proctoringOptions?.track?.recordSession,
            userDetails: {
                name: "First Last",
                email: "user@gmail.com",
            },
        };
    };

    return (
        <div className="flex flex-col gap-7">
            <Helmet>
                <script src={`https://cdn.autoproctor.co/ap-entry.js`}></script>
            </Helmet>

            {props.isAuxDevice ? (
                <>
                    <div id="aux-device-ctr"></div>
                    <div id="ap-proctoring-container"></div>
                </>
            ) : (
                <>
                    <div id="ap-proctoring-msg-container" className="hidden p-3 bg-yellow-50 w-full rounded-xl">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-5 h-5 text-yellow-400 mr-2"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm text-yellow-800 font-semibold">Proctoring has started </h3>
                            <p className="text-yellow-700 text-sm">
                                Block your camera, make some noise, switch the tab so AutoProctor can detect all these
                            </p>
                        </div>
                    </div>

                    <div
                        id="ap-proctoring-container"
                        className="container bg-black rounded-xl !p-3 w-full"
                        style={{
                            display: "none",
                        }}
                    >
                        <div className="text-white">
                            <div
                                id="ap-test-proctoring-status"
                                className="text-lg"
                                dangerouslySetInnerHTML={{ __html: proctoringStatus }}
                            ></div>
                            <div
                                id="proctor-feedback-ctr"
                                className="text-orange-400 text-lg h-6"
                                style={{ display: "none" }}
                            >
                                <div id="proctor-feedback" className="animate-pulse text-orange-700 inline"></div>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl p-5 w-full bg-gray-100 ">
                        <h2 className="text-2xl text-gray-800 font-bold mb-2">Run AutoProctor On This Page</h2>

                        <ul className="mb-2.5 list-decimal ml-8 text-base text-gray-700">
                            <li>Enable what you want tracked above</li>
                            <li>On clicking the Start button, a proctored session begins</li>
                            <li>After granting permissions, commit fake violations as prompted on the screen</li>
                            <li>Once you see that the violations are detected, click on the Stop button</li>
                            <li>The Proctoring Report automatically loads a few seconds later</li>
                            <li>You may change what is tracked and click on Start again for a new session</li>
                        </ul>

                        <div>
                            <button
                                className={
                                    "text-white text-xs py-2 px-6 ml-2 rounded-md mt-4 uppercase " +
                                    (!isStartDisabled
                                        ? "bg-emerald-600 border border-emerald-600 hover:bg-green-600 hover:text-neutral-100 "
                                        : "bg-emerald-600/30 border text-emerald-600/30 border-emerale-600 cursor-not-allowed")
                                }
                                onClick={() => {
                                    startTest();
                                    setShowReportButton(false);
                                }}
                                id="btn-start"
                                disabled={isStartDisabled}
                            >
                                Start
                            </button>
                            {!props?.isAuxDevice && (
                                <button
                                    className={
                                        "border text-xs py-2 px-6 ml-3 rounded-md mt-4 uppercase " +
                                        (!isStopDisabled
                                            ? "border-red-500 bg-red-500 hover:bg-red-600 text-neutral-100 hover:text-neutral-200 "
                                            : "border-red-500/30 text-red-500/30 bg-red-50 cursor-not-allowed")
                                    }
                                    onClick={() => {
                                        stopTest();
                                    }}
                                    id="btn-stop"
                                    disabled={isStopDisabled}
                                >
                                    Stop
                                </button>
                            )}
                            {showReportButton && (
                                <button
                                    className="text-gray-700 cursor-pointer border border-gray-500 mx-4 hover:bg-gray-200 hover:text-gray-900 text-xs py-2 px-6 ml-2 rounded-md mt-4 uppercase"
                                    onClick={() => {
                                        document.getElementById("ap-report-modal").style.display = "block";
                                    }}
                                >
                                    Show Report
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="rounded-xl p-5 w-full bg-gray-100">
                        <h2 className="text-2xl text-gray-800 font-bold mb-2">Run AutoProctor on Your Computer</h2>
                        <ul className="mb-2.5 list-decimal ml-8 text-base text-gray-700">
                            <li>Finish the above section!</li>
                            <li>
                                Watch this{" "}
                                <a
                                    className="cursor-pointer text-emerald-600 inline-flex gap-1 items-center"
                                    href="https://youtu.be/4aJFrTYrU3o"
                                    target="_blank"
                                >
                                    video
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-4 h-4 inline-block"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                        />
                                    </svg>
                                </a>{" "}
                                if you haven't already
                            </li>
                            <li>Your browser used the code on the right to conduct the proctoring</li>
                            <li>
                                You can copy-paste this code (all 3 tabs) on your local machine to conduct the same
                                proctoring
                            </li>
                            <li>
                                If you haven't already,{" "}
                                <a
                                    className="cursor-pointer text-emerald-600 inline-flex gap-1 items-center"
                                    href="/developers/register"
                                    target="_blank"
                                >
                                    get your key pair
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-4 h-4 inline-block"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                        />
                                    </svg>
                                </a>{" "}
                                first.{" "}
                                <span className="text-gray-700 font-bold">This code will not work otherwise!</span>
                            </li>
                            <li>
                                Once you have your key pair, replace the{" "}
                                <span className="border border-gray-300 rounded px-1">CLIENT_ID</span> and{" "}
                                <span className="border border-gray-300 rounded px-1">CLIENT_SECRET</span> as shown on
                                the JavaScript tab of the code
                            </li>
                            <li>
                                <a
                                    className="cursor-pointer text-emerald-600 inline-flex gap-1 items-center"
                                    href="/developers/docs"
                                    target="_blank"
                                >
                                    Read our documentation
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-4 h-4 inline-block"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                        />
                                    </svg>
                                </a>{" "}
                                to learn how the code works, and how you can get it to work for you
                            </li>
                        </ul>
                    </div>
                </>
            )}

            {showReportModal && (
                <ReportModal getReportOptions={getReportOptions} apInstance={apInstance} domain={props.domain} />
            )}
        </div>
    );
};

const styleForFrontCameraStream = `
    #frontCameraStreamHTMLElement {
        position: fixed;
        bottom: 5%;
        left: 50%;
        translate: -50% ;
    }
    .frontCameraStreamCtr {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        backdrop-filter: blur(10px);
        background-color: rgba(0, 0, 0, 0.05);
        z-index: 50;
    }
`;

export default ResultingUI;
